import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Save, ShieldCheck, UserCog, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Spinner } from "./common";
import { api, errMsg } from "../lib/api";

/**
 * Phase 174 — Super Admin: assign one or more staff roles to the same person.
 *
 * GET  /t/{code}/users/{userId}/roles  → {person_id, name, email, mobile, roles:[{role,label,user_id,is_active,is_current}], available:[{role,label}], notes}
 * PUT  /t/{code}/users/{userId}/roles  {roles:[...]}  → same shape (creates / re-activates / deactivates role accounts)
 */
const ROLE_ICON = { municipal_admin: ShieldCheck, supervisor: UserCog, tax_collector: Wallet };
const TERRITORY_ROLES = ["supervisor", "tax_collector"];   // roles that need zones / wards before they are useful
const ROLE_HINT = {
  municipal_admin: "Full municipality administration — masters, uploads, users, reports.",
  supervisor: "Zone-level console — assignments, field visits, approvals for mapped zones.",
  tax_collector: "Mobile field app — assigned properties, collections, surveys.",
};

export const ManageRolesDialog = ({ code, userId, open, onOpenChange, onSaved, onMapTerritory }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !code || !userId) return undefined;
    let alive = true;
    setData(null); setError(null);
    api.get(`/t/${code}/users/${userId}/roles`)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setSelected(r.data.active_roles || r.data.roles.filter((x) => x.is_active).map((x) => x.role));
      })
      .catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
  }, [open, code, userId]);

  const byRole = useMemo(() => Object.fromEntries((data?.roles || []).map((r) => [r.role, r])), [data]);
  const initial = useMemo(() => new Set(data?.active_roles || []), [data]);
  const dirty = data ? (selected.length !== initial.size || selected.some((r) => !initial.has(r))) : false;
  const removing = data ? [...initial].filter((r) => !selected.includes(r)) : [];
  const adding = data ? selected.filter((r) => !initial.has(r)) : [];

  const toggle = (role, on) => {
    setSelected((s) => (on ? [...new Set([...s, role])] : s.filter((r) => r !== role)));
  };

  const save = async () => {
    if (selected.length === 0) { toast.error("At least one role must remain active"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/users/${userId}/roles`, { roles: selected });
      setData(r.data);
      setSelected(r.data.active_roles || []);
      const labelOf = (x) => byRole[x]?.label || data.available.find((a) => a.role === x)?.label || x;
      const parts = [];
      if (adding.length) parts.push(`added ${adding.map(labelOf).join(", ")}`);
      if (removing.length) parts.push(`removed ${removing.map(labelOf).join(", ")}`);
      // Newly added Supervisor / Tax Collector roles start without zones / wards — offer to map them right away.
      const toMap = (r.data.roles || []).filter((x) => adding.includes(x.role) && x.is_active && TERRITORY_ROLES.includes(x.role) && !(x.territory?.mapped));
      if (toMap.length && onMapTerritory) {
        toast.success(`Roles updated for ${r.data.name}`, {
          description: `${parts.join(" · ")}. ${toMap.map((x) => x.label).join(" and ")} ${toMap.length === 1 ? "has" : "have"} no territory yet.`,
          duration: 12000,
          action: {
            label: "Map territory now",
            onClick: () => onMapTerritory(toMap.map((x) => ({ id: x.user_id, name: r.data.name, employee_id: x.employee_id, role: x.role, label: x.label }))),
          },
        });
      } else {
        toast.success(`Roles updated for ${r.data.name}`, { description: parts.length ? parts.join(" · ") : undefined });
      }
      onSaved?.(r.data, { added: adding, removed: removing });
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="manage-roles-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage roles</DialogTitle>
          <DialogDescription>
            One sign-in, several roles. The person keeps the same e-mail, mobile and password and switches roles from the portal header.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2" data-testid="manage-roles-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        ) : !data ? <Spinner label="Loading roles..." /> : (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/60 px-3 py-2.5" data-testid="manage-roles-person">
              <p className="text-sm font-medium">{data.name}</p>
              <p className="text-xs text-muted-foreground">{data.email}{data.mobile ? ` · ${data.mobile}` : ""}</p>
            </div>

            <div className="space-y-2">
              {data.available.map((a) => {
                const acc = byRole[a.role];
                const Icon = ROLE_ICON[a.role] || UserCog;
                const checked = selected.includes(a.role);
                const isCurrent = acc?.is_current;
                const id = `role-${a.role}`;
                return (
                  <label key={a.role} htmlFor={id}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${checked ? "border-primary/40 bg-primary/5" : "hover:bg-muted/50"}`}
                    data-testid={`manage-roles-row-${a.role}`}>
                    <Checkbox id={id} checked={checked} onCheckedChange={(v) => toggle(a.role, !!v)} className="mt-0.5"
                      data-testid={`manage-roles-checkbox-${a.role}`} />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2 flex-wrap">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{a.label}</span>
                        {acc && acc.is_active && <Badge variant="outline" className="text-[10px] border-emerald-300 bg-emerald-50 text-emerald-800">Active</Badge>}
                        {acc && !acc.is_active && <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive · history kept</Badge>}
                        {isCurrent && <Badge variant="outline" className="text-[10px]">Selected account</Badge>}
                        {!acc && checked && <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">New</Badge>}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{ROLE_HINT[a.role]}</span>
                      {acc?.employee_id && <span className="block text-[11px] text-muted-foreground mt-0.5 font-mono">Employee {acc.employee_id}</span>}
                    </span>
                  </label>
                );
              })}
            </div>

            {removing.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="manage-roles-remove-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Removing {removing.map((x) => byRole[x]?.label || x).join(", ")} deactivates that role account. Collections, attendance and audit history stay intact and the role can be re-enabled later.</span>
              </div>
            )}

            {adding.length > 0 && (
              <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-start gap-2" data-testid="manage-roles-add-hint">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {adding.some((x) => TERRITORY_ROLES.includes(x))
                    ? "New Supervisor / Tax Collector roles start without a territory. After saving, use \u201cMap territory now\u201d in the confirmation to pick zones / wards straight away \u2014 or the Municipal Admin can do it later from Users & Staff."
                    : "The new role account shares the person's sign-in, photo and employee details; module access follows the role defaults."}
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="manage-roles-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={!data || !dirty || busy || selected.length === 0} data-testid="manage-roles-save-button">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save roles"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
